import PptxGenJS from "pptxgenjs";
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image';
import { saveAs } from "file-saver";
import { computeSmartArtLayout } from "../components/SmartArt/layouts";
import { nodeAlign } from "../components/SmartArt/smartArtUtils";
import { injectAnimations } from "../components/Animations/utils/pptxAnimationInject";
import { animObjectName } from "../components/Animations/utils/pptxAnimationXml";

// Slide dimensions in inches (16:9 aspect ratio - standard PowerPoint)
const SLIDE_WIDTH_INCHES = 10;
const SLIDE_HEIGHT_INCHES = 5.625;

// Reference dimensions from the app (in pixels)
const APP_WIDTH = 960;
const APP_HEIGHT = 540;

/**
 * Convert hex color to PptxGenJS RGB format
 * @param {string} color - Color string (hex, rgb, rgba, or named color)
 * @returns {string} RGB color in format "RRGGBB"
 */
const convertColor = (color) => {
  if (!color || color === 'transparent' || color === 'none') return null;
  
  // Handle rgb/rgba format
  if (color.includes("rgb")) {
    const match = color.match(/\d+\.?\d*/g);
    if (match && match.length >= 3) {
      const r = Math.min(255, Math.max(0, parseInt(match[0]))).toString(16).padStart(2, '0');
      const g = Math.min(255, Math.max(0, parseInt(match[1]))).toString(16).padStart(2, '0');
      const b = Math.min(255, Math.max(0, parseInt(match[2]))).toString(16).padStart(2, '0');
      return `${r}${g}${b}`.toUpperCase();
    }
    return "FFFFFF";
  }
  
  // Handle hsl/hsla format
  if (color.includes("hsl")) {
    const match = color.match(/\d+\.?\d*/g);
    if (match && match.length >= 3) {
      const h = parseFloat(match[0]) / 360;
      const s = parseFloat(match[1]) / 100;
      const l = parseFloat(match[2]) / 100;
      
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      
      let r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
      }
      
      return [
        Math.round(r * 255).toString(16).padStart(2, '0'),
        Math.round(g * 255).toString(16).padStart(2, '0'),
        Math.round(b * 255).toString(16).padStart(2, '0')
      ].join('').toUpperCase();
    }
    return "FFFFFF";
  }
  
  // Handle named colors
  const namedColors = {
    'black': '000000', 'white': 'FFFFFF', 'red': 'FF0000', 'green': '008000',
    'blue': '0000FF', 'yellow': 'FFFF00', 'cyan': '00FFFF', 'magenta': 'FF00FF',
    'gray': '808080', 'grey': '808080', 'silver': 'C0C0C0', 'maroon': '800000',
    'olive': '808000', 'purple': '800080', 'teal': '008080', 'navy': '000080',
    'orange': 'FFA500', 'pink': 'FFC0CB', 'brown': 'A52A2A', 'lime': '00FF00'
  };
  
  if (namedColors[color.toLowerCase()]) {
    return namedColors[color.toLowerCase()];
  }
  
  // Handle hex format
  let cleanHex = color.replace("#", "").trim();
  
  // Handle 3-character hex
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(c => c + c).join('');
  }
  
  // Handle 8-character hex (with alpha)
  if (cleanHex.length === 8) {
    cleanHex = cleanHex.substring(0, 6);
  }
  
  // Validate hex
  if (/^[0-9A-Fa-f]{6}$/.test(cleanHex)) {
    return cleanHex.toUpperCase();
  }
  
  return "FFFFFF";
};

/**
 * Extract alpha/opacity from color string
 * @param {string} color - Color string that might contain alpha
 * @returns {number} Opacity value between 0 and 100
 */
const extractOpacity = (color) => {
  if (!color) return 100;
  
  if (color.includes("rgba")) {
    const match = color.match(/\d+\.?\d*/g);
    if (match && match.length >= 4) {
      return Math.round(parseFloat(match[3]) * 100);
    }
  }
  
  if (color.includes("hsla")) {
    const match = color.match(/\d+\.?\d*/g);
    if (match && match.length >= 4) {
      return Math.round(parseFloat(match[3]) * 100);
    }
  }
  
  // Handle 8-character hex (with alpha)
  if (color.startsWith('#') && color.length === 9) {
    const alpha = parseInt(color.substring(7, 9), 16);
    return Math.round((alpha / 255) * 100);
  }
  
  return 100;
};

/**
 * Convert percentage or pixel string to inches for PPTX
 * The app stores positions as percentages like '3.5%', '15%', '93%'
 * These need to be converted to inches based on slide dimensions
 * @param {string|number} value - CSS value (e.g., "50%" or "100px")
 * @param {string} dimension - 'width' or 'height' to determine reference
 * @returns {number} Value in inches
 */
const convertToInches = (value, dimension = 'width') => {
  if (value === null || value === undefined || value === '') return 0;
  
  const refPixels = dimension === 'width' ? APP_WIDTH : APP_HEIGHT;
  const refInches = dimension === 'width' ? SLIDE_WIDTH_INCHES : SLIDE_HEIGHT_INCHES;
  
  // Handle numeric values (could be percentages or pixels)
  if (typeof value === 'number') {
    // If it's a number between 0 and 100, treat as percentage
    // Values less than 1 might already be inches or fractions
    if (value >= 0 && value <= 100) {
      return (value / 100) * refInches;
    }
    // Large numbers are likely pixels
    return (value / refPixels) * refInches;
  }
  
  const strValue = String(value).trim();
  
  // Handle percentage values (e.g., '50%', '3.5%')
  if (strValue.endsWith('%')) {
    const percent = parseFloat(strValue);
    if (!isNaN(percent)) {
      return (percent / 100) * refInches;
    }
    return 0;
  }
  
  // Handle pixel values (e.g., '100px')
  if (strValue.endsWith('px')) {
    const pixels = parseFloat(strValue);
    if (!isNaN(pixels)) {
      return (pixels / refPixels) * refInches;
    }
    return 0;
  }
  
  // Handle em/rem values
  if (strValue.includes('em') || strValue.includes('rem')) {
    const ems = parseFloat(strValue);
    if (!isNaN(ems)) {
      const pixels = ems * 16;
      return (pixels / refPixels) * refInches;
    }
    return 0;
  }
  
  // Handle viewport units
  if (strValue.includes('vw')) {
    const vw = parseFloat(strValue);
    if (!isNaN(vw)) {
      return (vw / 100) * SLIDE_WIDTH_INCHES;
    }
    return 0;
  }
  
  if (strValue.includes('vh')) {
    const vh = parseFloat(strValue);
    if (!isNaN(vh)) {
      return (vh / 100) * SLIDE_HEIGHT_INCHES;
    }
    return 0;
  }
  
  // If it's just a number string, try to parse it
  const numValue = parseFloat(strValue);
  if (!isNaN(numValue)) {
    // Assume percentage if value is in typical percentage range
    if (numValue >= 0 && numValue <= 100) {
      return (numValue / 100) * refInches;
    }
    // Otherwise assume pixels
    return (numValue / refPixels) * refInches;
  }
  
  return 0;
};

/**
 * Convert font size to points for PowerPoint
 * Font sizes in Quill are stored as '10px', '14px', etc.
 * Shape.js defines: ['10px', '11px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '26px', '28px', '30px', '32px', '34px', '36px', '38px', '40px', '42px', '44px', '46px', '48px', '50px', '52px', '54px', '56px', '58px', '60px', '62px', '64px', '72px', '96px']
 * PowerPoint uses points - we convert pixels to points (1px ≈ 0.75pt at 96 DPI)
 * @param {string|number} size - Font size value
 * @returns {number} Font size in points
 */
const convertFontSize = (size) => {
  if (!size) return 42; // Default to 42pt for better readability
  
  const strSize = String(size).toLowerCase().trim();
  
  // Named sizes - Quill uses these sometimes
  const sizeMap = {
    'xx-small': 9, 'x-small': 10, 'small': 12, 'smaller': 13,
    'medium': 16, 'large': 20, 'larger': 22, 'x-large': 28,
    'xx-large': 36, 'huge': 48
  };
  
  if (sizeMap[strSize]) {
    return sizeMap[strSize];
  }
  
  // Pixel values - Quill stores as '10px', '14px', etc.
  // The font sizes match the whitelist in Shape.js
  // For PowerPoint, keep the numeric value as-is since px and pt render similarly
  if (strSize.includes('px')) {
    const pxValue = parseFloat(strSize);
    if (!isNaN(pxValue) && pxValue > 0) {
      // Keep the same numeric value - 14px becomes 14pt
      // This matches what users see on screen in the app
      return Math.round(pxValue);
    }
    return 42;
  }
  
  // Point values - use as-is
  if (strSize.includes('pt')) {
    const ptValue = parseFloat(strSize);
    return !isNaN(ptValue) && ptValue > 0 ? Math.round(ptValue) : 42;
  }
  
  // Em/rem values - assume 16px base
  if (strSize.includes('em') || strSize.includes('rem')) {
    const emValue = parseFloat(strSize);
    return !isNaN(emValue) && emValue > 0 ? Math.round(emValue * 16) : 42;
  }
  
  // Numeric value (assume pixels/points)
  const numSize = parseFloat(strSize);
  if (!isNaN(numSize) && numSize > 0) {
    return Math.round(numSize);
  }
  
  return 42;
};

/**
 * Parse Quill delta format to extract text with formatting
 * Quill stores formatting in delta.ops[].attributes
 * - Font: stored as 'font' attribute with font family name (from attributors/style/font)
 * - Size: stored as 'size' attribute with value like '14px' (from attributors/style/size)
 * - Color: stored as 'color' attribute
 * - Background: stored as 'background' attribute
 * 
 * NOTE: When user doesn't explicitly apply fonts/sizes, Quill doesn't store these
 * attributes - the text inherits from CSS. We extract defaults from containerStyles.
 * 
 * @param {Object} delta - Quill delta object
 * @param {Object} containerStyles - Container/textbox styles for default formatting
 * @returns {Array} Array of text objects with formatting for PptxGenJS
 */
const parseDeltaToText = (delta, containerStyles = {}) => {
  if (!delta || !delta.ops) return [];
  
  // Extract default font from container CSS styles (font-family or fontFamily)
  // Note: The app uses Roboto as the default font in the editor (from fontFamilyArr)
  let defaultFont = 'Roboto';
  if (containerStyles.fontFamily) {
    defaultFont = String(containerStyles.fontFamily).replace(/['"]/g, '').split(',')[0].trim();
  } else if (containerStyles['font-family']) {
    defaultFont = String(containerStyles['font-family']).replace(/['"]/g, '').split(',')[0].trim();
  }
  
  // Extract default font size from container CSS styles
  // The app uses 42px as the common default size for presentations
  let defaultFontSize = 42;
  if (containerStyles.fontSize) {
    defaultFontSize = convertFontSize(containerStyles.fontSize);
  } else if (containerStyles['font-size']) {
    defaultFontSize = convertFontSize(containerStyles['font-size']);
  }
  
  // Extract default text color from container CSS styles
  let defaultColor = '000000';
  if (containerStyles.color) {
    const converted = convertColor(containerStyles.color);
    if (converted) defaultColor = converted;
  }
  
  
  const textRuns = [];
  let currentParagraphAlign = null;
  
  // Get container-level default alignment
  const containerAlign = containerStyles.align || containerStyles.textAlign || null;
  
  
  delta.ops.forEach((op, index) => {
    // Handle newlines with paragraph formatting
    if (op.insert && typeof op.insert === 'string') {
      const text = op.insert;
      const attributes = op.attributes || {};
      
      
      // Check if this is a newline with paragraph formatting
      if (text === '\n' && attributes.align) {
        currentParagraphAlign = attributes.align;
      }
      
      // Skip completely empty strings (but keep newlines)
      if (text === '') return;
      
      const textRun = {
        text: text,
        options: {}
      };
      
      // Font size - Quill stores as '10px', '14px', etc. in the 'size' attribute
      if (attributes.size) {
        textRun.options.fontSize = convertFontSize(attributes.size);
      } else {
        textRun.options.fontSize = defaultFontSize; // Use container default
      }
      
      // Font family - Quill stores the font family name in the 'font' attribute
      // When using attributors/style/font, it stores the exact font name
      if (attributes.font) {
        // Clean font name - remove quotes and trim (handle both single and double quotes)
        let fontName = String(attributes.font).replace(/['"]/g, '').trim();
        // Map common fonts to PowerPoint-safe equivalents
        const fontMap = {
          'sans-serif': 'Arial',
          'serif': 'Times New Roman',
          'monospace': 'Courier New',
          'roboto': 'Roboto',
          'roboto condensed': 'Roboto Condensed',
          'times new roman': 'Times New Roman',
          'calibri': 'Calibri',
          'calibri light': 'Calibri Light',
          'cambria': 'Cambria',
          'georgia': 'Georgia',
          'verdana': 'Verdana',
          'arial': 'Arial',
          'raleway': 'Raleway',
          'helvetica': 'Helvetica',
          'tahoma': 'Tahoma',
          'trebuchet ms': 'Trebuchet MS',
          'comic sans ms': 'Comic Sans MS',
          'impact': 'Impact',
          'lucida console': 'Lucida Console',
          'palatino linotype': 'Palatino Linotype',
          'book antiqua': 'Book Antiqua',
          'garamond': 'Garamond',
          'century gothic': 'Century Gothic',
          'franklin gothic medium': 'Franklin Gothic Medium',
          'open sans': 'Open Sans',
          'lato': 'Lato',
          'montserrat': 'Montserrat',
          'source sans pro': 'Source Sans Pro',
          'oswald': 'Oswald',
          'nunito': 'Nunito',
          'poppins': 'Poppins'
        };
        const lowerFont = fontName.toLowerCase();
        textRun.options.fontFace = fontMap[lowerFont] || fontName;
      } else {
        // Use container default font (extracted from CSS)
        const fontMap = {
          'sans-serif': 'Arial',
          'serif': 'Times New Roman',
          'monospace': 'Courier New',
          'roboto': 'Roboto',
          'roboto condensed': 'Roboto Condensed',
          'times new roman': 'Times New Roman',
          'calibri': 'Calibri',
          'calibri light': 'Calibri Light',
          'cambria': 'Cambria',
          'georgia': 'Georgia',
          'verdana': 'Verdana',
          'arial': 'Arial',
          'raleway': 'Raleway'
        };
        const lowerFont = defaultFont.toLowerCase();
        textRun.options.fontFace = fontMap[lowerFont] || defaultFont;
      }
      
      // Text color - Quill stores as hex or rgb string
      if (attributes.color) {
        const color = convertColor(attributes.color);
        if (color) {
          textRun.options.color = color;
        } else {
          textRun.options.color = defaultColor;
        }
      } else {
        // Use container default color
        textRun.options.color = defaultColor;
      }
      
      // Bold
      if (attributes.bold) {
        textRun.options.bold = true;
      }
      
      // Italic
      if (attributes.italic) {
        textRun.options.italic = true;
      }
      
      // Underline
      if (attributes.underline) {
        textRun.options.underline = { style: 'sng' };
      }
      
      // Strike-through
      if (attributes.strike) {
        textRun.options.strike = 'sngStrike';
      }
      
      // Subscript
      if (attributes.script === 'sub') {
        textRun.options.subscript = true;
      }
      
      // Superscript
      if (attributes.script === 'super') {
        textRun.options.superscript = true;
      }
      
      // Background color (highlight) - Quill stores in 'background' attribute
      if (attributes.background) {
        const bgColor = convertColor(attributes.background);
        if (bgColor && bgColor !== 'FFFFFF') {
          textRun.options.highlight = bgColor;
        }
      }
      
      // Link
      if (attributes.link) {
        textRun.options.hyperlink = { url: attributes.link };
      }
      
      // Header formatting - increase font size based on header level
      if (attributes.header) {
        const headerSizes = { 1: 44, 2: 32, 3: 28, 4: 24, 5: 20, 6: 18 };
        textRun.options.fontSize = headerSizes[attributes.header] || 42;
        textRun.options.bold = true;
      }
      
      // Apply alignment priority: attributes > paragraph > container
      if (attributes.align) {
        textRun.options.align = attributes.align;
      } else if (currentParagraphAlign) {
        textRun.options.align = currentParagraphAlign;
      } else if (containerAlign) {
        textRun.options.align = containerAlign;
      }
      
      // Line height - Quill stores in 'lineheight' attribute
      if (attributes.lineheight) {
        const lineHeight = parseFloat(attributes.lineheight);
        if (!isNaN(lineHeight)) {
          textRun.options.lineSpacingMultiple = lineHeight;
        }
      }
      
      textRuns.push(textRun);
    }
    // Handle embedded objects (images in text, etc.)
    else if (op.insert && typeof op.insert === 'object') {
      // Handle image embeds
      if (op.insert.image) {
        // Images in text are complex - add placeholder text
        textRuns.push({
          text: '[Image]',
          options: { fontSize: 12, color: '808080' }
        });
      }
    }
  });
  
  return textRuns;
};

/**
 * Get text alignment from styles
 * @param {Object} styles - CSS styles object
 * @returns {string} Alignment value for PptxGenJS
 */
const getTextAlignment = (styles) => {
  if (!styles) return 'left';
  
  if (styles.textAlign) {
    const align = styles.textAlign.toLowerCase();
    if (['left', 'center', 'right', 'justify'].includes(align)) {
      return align === 'justify' ? 'justify' : align;
    }
  }
  
  if (styles.align) {
    const align = styles.align.toLowerCase();
    if (['left', 'center', 'right', 'justify'].includes(align)) {
      return align === 'justify' ? 'justify' : align;
    }
  }
  
  return 'left';
};

/**
 * Get vertical alignment from styles
 * @param {Object} styles - CSS styles object
 * @returns {string} Vertical alignment value for PptxGenJS
 */
const getVerticalAlignment = (styles) => {
  if (!styles) return 'top';
  
  const valign = styles.verticalAlign || styles.alignItems || '';
  
  switch (valign.toLowerCase()) {
    case 'middle':
    case 'center':
      return 'middle';
    case 'bottom':
    case 'flex-end':
      return 'bottom';
    default:
      return 'top';
  }
};

/**
 * Extract border properties from styles
 * Following Google Slides/PowerPoint standard: NO border by default
 * Only returns border if explicitly set with visible properties
 * @param {Object} styles - CSS styles object
 * @returns {Object|null} Border configuration for PptxGenJS or null if no border
 */
const extractBorder = (styles) => {
  if (!styles) return null;
  
  let borderColor = null;
  let borderWidth = 0;
  let borderStyle = 'solid';
  let hasExplicitBorder = false;
  
  // Check for shorthand border property first (most explicit)
  if (styles.border && typeof styles.border === 'string') {
    const borderLower = styles.border.toLowerCase().trim();
    
    // Explicitly no border
    if (borderLower === 'none' || borderLower === '0' || borderLower === 'transparent' || 
        borderLower === '0px' || borderLower === 'initial' || borderLower === 'unset') {
      return null;
    }
    
    // Parse border shorthand (e.g., "1px solid #000")
    const widthMatch = styles.border.match(/(\d+(?:\.\d+)?)\s*px/i);
    if (widthMatch) {
      const width = parseFloat(widthMatch[1]);
      if (width > 0) {
        borderWidth = width;
        hasExplicitBorder = true;
      }
    }
    
    const colorMatch = styles.border.match(/#[0-9A-Fa-f]{3,8}|rgb\([^)]+\)|rgba\([^)]+\)/i);
    if (colorMatch) {
      const color = convertColor(colorMatch[0]);
      if (color && color !== 'FFFFFF') {  // Ignore white borders as they're often invisible
        borderColor = color;
        hasExplicitBorder = true;
      }
    }
  }
  
  // Check individual border properties only if no shorthand
  if (!hasExplicitBorder) {
    // borderWidth must be explicitly set and > 0
    if (styles.borderWidth) {
      const width = parseFloat(styles.borderWidth);
      if (!isNaN(width) && width > 0) {
        borderWidth = width;
        hasExplicitBorder = true;
      }
    }
    
    // borderColor must be explicitly set and not transparent
    if (styles.borderColor && 
        styles.borderColor !== 'transparent' && 
        styles.borderColor !== 'none' &&
        styles.borderColor !== 'initial') {
      const color = convertColor(styles.borderColor);
      if (color) {
        borderColor = color;
        hasExplicitBorder = true;
      }
    }
    
    // borderStyle of 'none' means no border
    if (styles.borderStyle) {
      if (styles.borderStyle === 'none') {
        return null;
      }
      borderStyle = styles.borderStyle;
    }
  }
  
  // Only return a border if explicitly set with width > 0
  if (!hasExplicitBorder || borderWidth <= 0) {
    return null;
  }
  
  return {
    color: borderColor || '000000',
    pt: Math.max(0.5, borderWidth / 2),
    type: borderStyle === 'dashed' ? 'dash' : borderStyle === 'dotted' ? 'dot' : 'solid'
  };
};

/**
 * Determine shape type from shape ID
 * The app uses prefixes like 'circl', 'trian', 'recta', 'ovals', 'trape', 'diamo', 'right', 'callo', 'stars', 'cylin'
 * Based on Shape.js: getShapeById function maps shape IDs to types
 * @param {string} shapeId - The shape ID from the app
 * @param {Object} pptx - PptxGenJS instance for shape types
 * @returns {string} PptxGenJS shape type
 */
const getShapeType = (shapeId, pptx) => {
  const id = (shapeId || '').toLowerCase();
  
  // Check for right angle triangle first (before regular triangle)
  // Order matters - 'right' must be checked before 'trian'
  if (id.includes('right')) {
    return pptx.ShapeType.rtTriangle;
  }
  
  // Map shape prefixes to PptxGenJS shape types
  // These match the prefixes used in Shape.js getShapeById()
  
  // Circle - uses 'circl' prefix
  if (id.includes('circl')) {
    return pptx.ShapeType.ellipse;  // Circle is an ellipse with equal width/height
  }
  
  // Oval - uses 'ovals' prefix
  if (id.includes('ovals')) {
    return pptx.ShapeType.ellipse;
  }
  
  // Triangle - uses 'trian' prefix
  if (id.includes('trian')) {
    return pptx.ShapeType.triangle;
  }
  
  // Rectangle - uses 'recta' prefix
  if (id.includes('recta')) {
    return pptx.ShapeType.rect;
  }
  
  // Rounded rectangle - uses 'round' prefix
  if (id.includes('round')) {
    return pptx.ShapeType.roundRect;
  }
  
  // Diamond - uses 'diamo' prefix
  if (id.includes('diamo')) {
    return pptx.ShapeType.diamond;
  }
  
  // Trapezoid - uses 'trape' prefix
  if (id.includes('trape')) {
    return pptx.ShapeType.trapezoid;
  }
  
  // Parallelogram - uses 'paral' prefix
  if (id.includes('paral')) {
    return pptx.ShapeType.parallelogram;
  }
  
  // Star - uses 'stars' prefix
  if (id.includes('stars')) {
    return pptx.ShapeType.star5;
  }
  
  // Heart - uses 'heart' prefix
  if (id.includes('heart')) {
    return pptx.ShapeType.heart;
  }
  
  // Plus/Cross - uses 'pluss' or 'plus' prefix
  if (id.includes('pluss') || id.includes('plus')) {
    return pptx.ShapeType.plus;
  }
  
  // Arrow
  if (id.includes('arrow')) {
    return pptx.ShapeType.rightArrow;
  }
  
  // Callout - uses 'callo' prefix
  if (id.includes('callo')) {
    return pptx.ShapeType.wedgeRectCallout;
  }
  
  // Cylinder - uses 'cylin' prefix
  if (id.includes('cylin')) {
    return pptx.ShapeType.can;
  }
  
  // Square - uses 'squr2' or 'square' prefix
  if (id.includes('squr2') || id.includes('squr') || id.includes('square')) {
    return pptx.ShapeType.rect;
  }
  
  // Single corner rectangle - uses 'singl' prefix
  if (id.includes('singl')) {
    return pptx.ShapeType.roundRect;
  }
  
  // Line
  if (id.includes('line')) {
    return pptx.ShapeType.line;
  }
  
  // Hexagon
  if (id.includes('hexag')) {
    return pptx.ShapeType.hexagon;
  }
  
  // Pentagon
  if (id.includes('penta')) {
    return pptx.ShapeType.pentagon;
  }
  
  // Default to rectangle for unknown shapes
  console.warn(`Unknown shape type for ID: ${shapeId}, defaulting to rectangle`);
  return pptx.ShapeType.rect;
};

/**
 * Parse rotation from transform style
 * @param {string} transform - CSS transform value
 * @returns {number} Rotation in degrees
 */
const parseRotation = (transform) => {
  if (!transform) return 0;
  
  const rotateMatch = transform.match(/rotate\(([^)]+)\)/);
  if (rotateMatch) {
    const value = rotateMatch[1];
    if (value.includes('deg')) {
      return parseFloat(value);
    }
    if (value.includes('rad')) {
      return parseFloat(value) * (180 / Math.PI);
    }
    return parseFloat(value);
  }
  
  return 0;
};

/**
 * Process and validate image data
 * @param {string} imageData - Base64 image data or URL
 * @returns {Object} Processed image data with type
 */
const processImageData = (imageData) => {
  if (!imageData) return null;
  
  // Check if it's a base64 data URL
  if (imageData.startsWith('data:')) {
    const match = imageData.match(/^data:image\/(\w+);base64,/);
    if (match) {
      return {
        data: imageData,
        type: match[1].toLowerCase()
      };
    }
  }
  
  // Check if it's a URL
  if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
    // Determine type from URL extension
    const ext = imageData.split('.').pop().toLowerCase().split('?')[0];
    const typeMap = { 'jpg': 'jpeg', 'jpeg': 'jpeg', 'png': 'png', 'gif': 'gif', 'svg': 'svg', 'webp': 'png' };
    return {
      path: imageData,
      type: typeMap[ext] || 'png'
    };
  }
  
  // Assume it's base64 without prefix (add PNG prefix as default)
  if (imageData.length > 100 && !imageData.includes(' ')) {
    return {
      data: `data:image/png;base64,${imageData}`,
      type: 'png'
    };
  }
  
  return null;
};

/**
 * Convert blob URL to base64 data URL
 * @param {string} blobUrl - Blob URL to convert
 * @returns {string|null} Base64 data URL or null if failed
 */
const convertBlobUrlToBase64 = async (blobUrl) => {
  if (!blobUrl || !blobUrl.startsWith('blob:')) return blobUrl;
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting blob URL to base64:', error);
    return null;
  }
};

/**
 * Export slides to PowerPoint (.pptx) format - Google Slides quality
 * @param {Object} slides - Slides data from Redux store
 * @param {string} fileName - Name for the exported file
 * @param {Object} options - Export options
 * @returns {Promise<Object>} Result object with success status and message
 */
export const exportToPPTX = async (slides, fileName = "presentation", options = {}) => {
  try {
    if (!slides || Object.keys(slides).length === 0) {
      return { success: false, message: "No slides to export." };
    }
    
    const pptx = new PptxGenJS();
    
    // Set presentation properties
    pptx.author = options.author || "Slide";
    pptx.company = options.company || "Slide";
    pptx.title = fileName;
    pptx.subject = options.subject || "Presentation";
    pptx.revision = "1";
    
    // Set slide dimensions (16:9 aspect ratio - standard PowerPoint size)
    pptx.defineLayout({ name: 'CUSTOM_16x9', width: SLIDE_WIDTH_INCHES, height: SLIDE_HEIGHT_INCHES });
    pptx.layout = 'CUSTOM_16x9';
    
    // Filter out master slides from export (unless specifically requested)
    // IMPORTANT: Object.keys() preserves insertion order in modern JavaScript (ES2015+)
    // This maintains the original slide sequence as displayed in the app
    const allSlideKeys = Object.keys(slides);
    const slideKeys = options.includeMasterSlides 
      ? allSlideKeys 
      : allSlideKeys.filter(key => !key.toLowerCase().includes('master'));
    
    // Create exportSlides object preserving the original order
    const exportSlides = {};
    slideKeys.forEach(key => {
      exportSlides[key] = slides[key];
    });
    
    if (slideKeys.length === 0) {
      return { success: false, message: "No valid slides to export (master slides excluded)." };
    }
    
    
    // Process each slide
    for (let slideIndex = 0; slideIndex < slideKeys.length; slideIndex++) {
      const slideId = slideKeys[slideIndex];
      const slideData = exportSlides[slideId];
      
      if (!slideData) {
        console.warn(`Slide ${slideId} has no data, skipping...`);
        continue;
      }
      
      try {
        const slide = pptx.addSlide();
        
        // Set slide background - default to white
        slide.background = { color: 'FFFFFF' };
        
        // Override with slide background color if specified
        if (slideData.slideBackgroundColor && 
            slideData.slideBackgroundColor !== 'transparent' &&
            slideData.slideBackgroundColor !== 'none' &&
            slideData.slideBackgroundColor !== '#fff' &&
            slideData.slideBackgroundColor !== '#ffffff') {
          const bgColor = convertColor(slideData.slideBackgroundColor);
          if (bgColor && bgColor !== 'FFFFFF') {
            slide.background = { color: bgColor };
          }
        }
        
        // Handle background gradient if present (linear gradient support)
        if (slideData.backgroundGradient) {
          const gradient = slideData.backgroundGradient;
          if (gradient.colors && gradient.colors.length >= 2) {
            // PptxGenJS supports gradients
            try {
              slide.background = {
                color: convertColor(gradient.colors[0]) || 'FFFFFF',
                // TODO: Full gradient support when PptxGenJS adds it
              };
            } catch (gradErr) {
              console.warn('Gradient fallback to solid color:', gradErr);
            }
          }
        }
        
        // Handle CSS gradient string (e.g., 'linear-gradient(...)')
        if (typeof slideData.slideBackgroundColor === 'string' && 
            slideData.slideBackgroundColor.includes('gradient')) {
          // Extract first color from gradient
          const colorMatch = slideData.slideBackgroundColor.match(/#[0-9A-Fa-f]{3,8}|rgb\([^)]+\)|rgba\([^)]+\)/);
          if (colorMatch) {
            const gradientColor = convertColor(colorMatch[0]);
            if (gradientColor) {
              slide.background = { color: gradientColor };
            }
          }
        }
        
        // Handle background image if present
        if (slideData.backgroundImage) {
          const imgData = processImageData(slideData.backgroundImage);
          if (imgData) {
            try {
              slide.background = imgData.data ? { data: imgData.data } : { path: imgData.path };
            } catch (bgErr) {
              console.warn(`Could not set background image for slide ${slideId}:`, bgErr);
            }
          }
        }
        
        // Collect all elements for z-index sorting
        const allElements = [];
        
        // Collect text boxes - check both textBoxIds array and textBox object
        if (slideData.textBox) {
          const textBoxIds = slideData.textBoxIds || Object.keys(slideData.textBox);
          textBoxIds.forEach(textId => {
            const textBox = slideData.textBox[textId];
            if (!textBox) return;
            
            const styles = textBox.styles || {};
            const zIndex = parseInt(styles.zIndex) || 0;
            
            allElements.push({
              type: 'text',
              id: textId,
              zIndex: zIndex,
              data: textBox
            });
          });
        }
        
        // Collect images - check both imageIds array and image object
        if (slideData.image) {
          const imageIds = slideData.imageIds || Object.keys(slideData.image);
          imageIds.forEach(imageId => {
            const image = slideData.image[imageId];
            if (!image) return;
            
            const styles = image.styles || {};
            const zIndex = parseInt(styles.zIndex) || 0;
            
            allElements.push({
              type: 'image',
              id: imageId,
              zIndex: zIndex,
              data: image
            });
          });
        }
        
        // Collect shapes
        if (slideData.shapes) {
          Object.keys(slideData.shapes).forEach(shapeId => {
            const shape = slideData.shapes[shapeId];
            if (!shape) return;
            
            const styles = shape.styles || {};
            const zIndex = parseInt(styles.zIndex) || 0;
            
            allElements.push({
              type: 'shape',
              id: shapeId,
              zIndex: zIndex,
              data: shape
            });
          });
        }
        
        
        // Collect SmartArt objects
        if (slideData.smartArtObjects) {
          Object.keys(slideData.smartArtObjects).forEach(smartArtId => {
            const smartArt = slideData.smartArtObjects[smartArtId];
            if (!smartArt) return;

            const styles = smartArt.styles || {};
            const zIndex = parseInt(styles.zIndex) || 0;

            allElements.push({
              type: 'smartart',
              id: smartArtId,
              zIndex: zIndex,
              data: smartArt
            });
          });
        }

        // Sort elements by z-index (ascending - lower z-index renders first/behind)
        allElements.sort((a, b) => a.zIndex - b.zIndex);
        
        // Add elements in z-index order
        for (const element of allElements) {
          try {
            if (element.type === 'text') {
              addTextElement(slide, element, pptx, slideData);
            } else if (element.type === 'image') {
              await addImageElement(slide, element, slideData);
            } else if (element.type === 'shape') {
              addShapeElement(slide, element, pptx, slideData);
            } else if (element.type === 'smartart') {
              addSmartArtElement(slide, element, pptx);
            }
          } catch (elementError) {
            console.error(`Error adding ${element.type} element ${element.id}:`, elementError);
            // Continue with other elements
          }
        }
        
        // Add speaker notes if available
        if (slideData.speakerNotes && typeof slideData.speakerNotes === 'string') {
          slide.addNotes(slideData.speakerNotes);
        }
        
        
      } catch (slideError) {
        console.error(`Error processing slide ${slideId}:`, slideError);
        // Continue with other slides
      }
    }
    
    // Generate the file. PptxGenJS cannot write animation timing, so the file is produced
    // as a blob, timing is injected into the slide parts, and the result is saved.
    const outputFileName = fileName.endsWith('.pptx') ? fileName.slice(0, -5) : fileName;
    const rawBlob = await pptx.write({ outputType: 'blob' });
    const finalBlob = await injectAnimations(
      rawBlob,
      slideKeys.map((slideId) => ({ slideId, slideData: exportSlides[slideId] }))
    );
    saveAs(finalBlob, `${outputFileName}.pptx`);

    return {
      success: true,
      message: `PowerPoint file exported successfully! (${slideKeys.length} slides)`
    };
    
  } catch (error) {
    console.error("Error exporting to PPTX:", error);
    return { 
      success: false, 
      message: `Failed to export PowerPoint file: ${error.message}`, 
      error 
    };
  }
};

/**
 * Placeholder text patterns used in layout templates
 * These should not be exported - following Google Slides/PowerPoint standard
 */
const PLACEHOLDER_PATTERNS = [
  'click to add title',
  'click to add subtitle',
  'click to add text',
  'click to add notes',
  'click to add content',
  'add title',
  'add subtitle',
  'add text'
];

/**
 * Check if text content is a placeholder that should not be exported
 * @param {Object} delta - Quill delta object
 * @returns {boolean} True if content is placeholder text
 */
const isPlaceholderText = (delta) => {
  if (!delta || !delta.ops || delta.ops.length === 0) return true;
  
  // Extract all text from delta
  let fullText = '';
  delta.ops.forEach(op => {
    if (op.insert && typeof op.insert === 'string') {
      fullText += op.insert;
    }
  });
  
  // Clean up the text
  const cleanText = fullText.trim().toLowerCase();
  
  // Empty text is considered placeholder
  if (cleanText === '' || cleanText === '\n') return true;
  
  // Check against placeholder patterns
  return PLACEHOLDER_PATTERNS.some(pattern => cleanText === pattern || cleanText.startsWith(pattern));
};

/**
 * Check if textbox is a layout template element
 * @param {string} elementId - The element ID
 * @returns {boolean} True if it's a layout element
 */
const isLayoutElement = (elementId) => {
  if (!elementId) return false;
  const id = elementId.toLowerCase();
  return id.includes('layout') || id.startsWith('title-') || id.startsWith('subtitle-') || id.includes('default');
};

/**
 * Get element properties from slideData.additionalData
 * @param {Object} slideData - The slide data object
 * @param {string} elementId - The element ID
 * @returns {Object} Element properties
 */
const getElementProperties = (slideData, elementId) => {
  if (!slideData || !slideData.additionalData || !slideData.additionalData.elementsData) {
    return {};
  }
  return slideData.additionalData.elementsData[elementId]?.properties || {};
};

/**
 * Add a text element to the slide
 * @param {Object} slide - PptxGenJS slide object
 * @param {Object} element - Element data with type, id, zIndex, data
 * @param {Object} pptx - PptxGenJS instance
 * @param {Object} slideData - Full slide data including additionalData
 */
const addTextElement = (slide, element, pptx, slideData) => {
  const textBox = element.data;
  const styles = textBox.styles || {};
  const elementProps = getElementProperties(slideData, element.id);
  
  // Calculate position and size in inches
  // Styles are stored as percentages like 'left: 3.5%', 'top: 15%', 'width: 93%'
  let x = convertToInches(styles.left, 'width');
  let y = convertToInches(styles.top, 'height');
  let w = convertToInches(styles.width, 'width');
  let h = convertToInches(styles.height, 'height');
  
  // Apply minimum dimensions
  w = Math.max(w, 0.5);
  h = Math.max(h, 0.25);
  
  // Clamp to slide boundaries but allow some overflow
  x = Math.max(0, x);
  y = Math.max(0, y);
  
  // Ensure element doesn't extend too far beyond slide
  if (x > SLIDE_WIDTH_INCHES - 0.25) {
    x = SLIDE_WIDTH_INCHES - 0.5;
  }
  if (y > SLIDE_HEIGHT_INCHES - 0.25) {
    y = SLIDE_HEIGHT_INCHES - 0.5;
  }
  
  // Parse delta to get formatted text
  const textRuns = parseDeltaToText(textBox.delta, styles);
  
  if (textRuns.length === 0) {
    // No text content, but might have background - add empty placeholder
    const bgColor = convertColor(elementProps.backgroundColor || styles.backgroundColor);
    if (bgColor && bgColor !== 'FFFFFF') {
      slide.addShape(pptx.ShapeType.rect, {
        x: x,
        y: y,
        w: w,
        h: h,
        fill: { color: bgColor }
      });
    }
    return;
  }
  
  // Build text options
  const textOptions = {
    x: x,
    y: y,
    w: w,
    h: h,
    valign: getVerticalAlignment(styles),
    align: getTextAlignment(styles),
    wrap: true,
    shrinkText: false,
    autoFit: false,
    // Tags the shape so the animation pass can find the id PptxGenJS assigns it.
    objectName: animObjectName(element.id)
  };
  
  // Set background color - check elementProps first, then styles
  const backgroundColor = elementProps.backgroundColor || styles.backgroundColor;
  if (backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== 'none') {
    const bgColor = convertColor(backgroundColor);
    if (bgColor) {
      textOptions.fill = { color: bgColor };
    }
  }
  
  // Border handling following Google Slides standard:
  // - Layout placeholder textboxes get a light gray dashed border
  // - Regular textboxes have NO border by default
  // - Explicitly styled borders are always applied
  const border = extractBorder(styles);
  if (border && border.pt > 0) {
    // User explicitly set a border - use it
    textOptions.line = border;
  } else if (isPlaceholderText(textBox.delta)) {
    // Google Slides standard: placeholder textboxes have light gray dashed border
    textOptions.line = {
      color: '999999',  // Medium gray for better visibility
      pt: 1.0,
      dashType: 'sysDash'
    };
  }
  // Regular textboxes: no border by default (matching Google Slides/PowerPoint)
  
  // Handle rotation from elementProps or transform
  const rotation = elementProps.rotation || parseRotation(styles.transform);
  if (rotation && rotation !== 0) {
    textOptions.rotate = rotation;
  }
  
  // Handle opacity
  const opacity = elementProps.opacity !== undefined ? elementProps.opacity / 100 : parseFloat(styles.opacity);
  if (!isNaN(opacity) && opacity < 1) {
    textOptions.transparency = Math.round((1 - opacity) * 100);
  }
  
  // Handle padding
  if (styles.padding) {
    const paddingValue = parseFloat(styles.padding);
    if (!isNaN(paddingValue)) {
      const paddingInches = (paddingValue / 100) * SLIDE_WIDTH_INCHES;
      textOptions.margin = paddingInches;
    }
  }
  
  // Add text with formatting
  slide.addText(textRuns, textOptions);
};

/**
 * Add an image element to the slide
 * @param {Object} slide - PptxGenJS slide object
 * @param {Object} element - Element data with type, id, zIndex, data
 * @param {Object} slideData - Full slide data including additionalData
 */
const addImageElement = async (slide, element, slideData) => {
  const image = element.data;
  const elementProps = getElementProperties(slideData, element.id);
  
  // Check for image data
  let imageData = image.imageb64 || image.src || image.url;
  if (!imageData) {
    console.warn(`Image ${element.id} has no image data`);
    return;
  }
  
  // Convert blob URLs to base64 data URLs
  if (imageData.startsWith('blob:')) {
    imageData = await convertBlobUrlToBase64(imageData);
    if (!imageData) {
      console.warn(`Failed to convert blob URL for image ${element.id}`);
      return;
    }
  }
  
  const processedImage = processImageData(imageData);
  if (!processedImage) {
    console.warn(`Could not process image data for ${element.id}`);
    return;
  }
  
  const styles = image.styles || {};
  
  // Calculate position and size in inches
  let x = convertToInches(styles.left, 'width');
  let y = convertToInches(styles.top, 'height');
  let w = convertToInches(styles.width, 'width');
  let h = convertToInches(styles.height, 'height');
  
  // Use elementProps for width/height if available
  if (elementProps.width) {
    w = convertToInches(elementProps.width, 'width');
  }
  if (elementProps.height) {
    h = convertToInches(elementProps.height, 'height');
  }
  
  // Ensure valid positioning
  x = Math.max(0, x);
  y = Math.max(0, y);
  
  // Ensure minimum dimensions
  w = Math.max(w, 0.5);
  h = Math.max(h, 0.5);
  
  // Build image options
  const imageOptions = {
    x: x,
    y: y,
    w: w,
    h: h,
    sizing: { type: 'cover', w: w, h: h }
  };
  
  // Add image data
  if (processedImage.data) {
    imageOptions.data = processedImage.data;
  } else if (processedImage.path) {
    imageOptions.path = processedImage.path;
  }
  
  // Handle rotation from elementProps or transform
  const rotation = elementProps.rotation || parseRotation(styles.transform);
  if (rotation && rotation !== 0) {
    imageOptions.rotate = rotation;
  }
  
  // Handle opacity from elementProps or styles
  const opacity = elementProps.opacity !== undefined ? elementProps.opacity / 100 : parseFloat(styles.opacity);
  if (!isNaN(opacity) && opacity < 1) {
    imageOptions.transparency = Math.round((1 - opacity) * 100);
  }
  
  // Handle border radius (for rounded images)
  const borderRadius = elementProps.borderRadius || parseFloat(styles.borderRadius);
  if (borderRadius && borderRadius > 0) {
    imageOptions.rounding = true;
  }
  
  // Handle border
  if (elementProps.strokeWidth && elementProps.strokeWidth > 0) {
    imageOptions.line = {
      color: convertColor(elementProps.borderColor) || '000000',
      pt: parseFloat(elementProps.strokeWidth) || 1
    };
  }
  
  // Handle shadow
  if (styles.boxShadow) {
    imageOptions.shadow = {
      type: 'outer',
      blur: 3,
      offset: 2,
      angle: 45,
      color: '000000',
      opacity: 0.3
    };
  }
  
  // Tags the shape so the animation pass can find the id PptxGenJS assigns it.
  imageOptions.objectName = animObjectName(element.id);

  try {
    slide.addImage(imageOptions);
  } catch (imgErr) {
    console.error(`Error adding image ${element.id}:`, imgErr);
  }
};

/**
 * Add a shape element to the slide
 * Shape properties are stored in slideData.additionalData.elementsData[shapeId].properties
 * These include: isTransparent, strokeWidth, backgroundColor, borderColor, strokeUnit, width, height, opacity, rotation
 * 
 * IMPORTANT: When a shape has text, we use slide.addText() with the `shape` option
 * This creates a grouped shape+text element like Google Slides, where text and shape
 * move/resize together as a single unit.
 * 
 * @param {Object} slide - PptxGenJS slide object
 * @param {Object} element - Element data with type, id, zIndex, data
 * @param {Object} pptx - PptxGenJS instance
 * @param {Object} slideData - Full slide data including additionalData
 */
/**
 * Flatten a SmartArt object into native PptxGenJS primitives: each node becomes a
 * shape-with-text, each connector a line. Geometry is computed by the same pure layout
 * engine the editor uses, then mapped from container-relative % to absolute slide inches.
 */
const addSmartArtElement = (slide, element, pptx) => {
  const obj = element.data;
  const styles = obj.styles || {};
  const baseX = Math.max(0, convertToInches(styles.left, 'width'));
  const baseY = Math.max(0, convertToInches(styles.top, 'height'));
  const baseW = convertToInches(styles.width, 'width') || SLIDE_WIDTH_INCHES;
  const baseH = convertToInches(styles.height, 'height') || SLIDE_HEIGHT_INCHES;

  const theme = obj.theme || {};
  const { boxes, connectors } = computeSmartArtLayout(obj.layout, obj.nodes || [], theme, obj.variant);
  const nodeText = (id) => (obj.nodes || []).find((n) => n.id === id)?.text || '';
  const nodeAlignFor = (id) => nodeAlign((obj.nodes || []).find((n) => n.id === id)?.delta);
  const connectorColor = convertColor(theme.connectorColor || '#9aa0a6') || '9AA0A6';

  // connectors first so node boxes paint on top
  connectors.forEach((c) => {
    const x1 = baseX + (c.x1Pct / 100) * baseW;
    const y1 = baseY + (c.y1Pct / 100) * baseH;
    const x2 = baseX + (c.x2Pct / 100) * baseW;
    const y2 = baseY + (c.y2Pct / 100) * baseH;

    // PowerPoint-style chunky block arrow (cycle layout): a filled rightArrow rotated to align.
    if (c.kind === 'blockArrow') {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const len = Math.hypot(dx, dy) || 0.01;
      const thick = Math.max(Math.min(baseW, baseH) * 0.09, 0.05);
      const mx = (x1 + x2) / 2;
      const my = (y1 + y2) / 2;
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      try {
        slide.addShape(pptx.ShapeType.rightArrow, {
          x: mx - len / 2,
          y: my - thick / 2,
          w: len,
          h: thick,
          rotate: angle,
          fill: { color: convertColor(theme.connectorColor) || convertColor(c.fill) || connectorColor },
          line: { type: 'none' },
        });
      } catch (e) {
        console.error('Error adding SmartArt block-arrow connector:', e);
      }
      return;
    }

    const lineOpts = {
      x: Math.min(x1, x2),
      y: Math.min(y1, y2),
      w: Math.max(Math.abs(x2 - x1), 0.01),
      h: Math.max(Math.abs(y2 - y1), 0.01),
      line: { color: connectorColor, width: 1 },
      flipV: (x2 - x1) * (y2 - y1) < 0, // "/" diagonal needs a vertical flip
    };
    if (c.kind === 'arrow') lineOpts.line.endArrowType = 'triangle';
    try {
      slide.addShape(pptx.ShapeType.line, lineOpts);
    } catch (e) {
      console.error('Error adding SmartArt connector:', e);
    }
  });

  boxes.forEach((box) => {
    const shapeType =
      box.shapeType === 'oval'
        ? pptx.ShapeType.ellipse
        : box.shapeType === 'trapezoid'
          ? pptx.ShapeType.trapezoid
          : pptx.ShapeType.roundRect;
    const opts = {
      x: baseX + (box.xPct / 100) * baseW,
      y: baseY + (box.yPct / 100) * baseH,
      w: Math.max((box.wPct / 100) * baseW, 0.1),
      h: Math.max((box.hPct / 100) * baseH, 0.1),
      shape: shapeType,
      fill: { color: convertColor(box.fill) || '4285F4' },
      line: { type: 'none' },
      align: nodeAlignFor(box.nodeId),
      valign: 'middle',
      wrap: true,
      color: convertColor(box.textColor) || 'FFFFFF',
      fontSize: 12,
      // Tags the shape so the animation pass can find the id PptxGenJS assigns it.
      objectName: animObjectName(element.id, box.nodeId),
    };
    try {
      slide.addText(nodeText(box.nodeId) || ' ', opts);
    } catch (e) {
      console.error('Error adding SmartArt node:', e);
    }
  });
};

const addShapeElement = (slide, element, pptx, slideData) => {
  const shape = element.data;
  const styles = shape.styles || {};
  const elementProps = getElementProperties(slideData, element.id);
  
  
  // Calculate position and size in inches
  let x = convertToInches(styles.left, 'width');
  let y = convertToInches(styles.top, 'height');
  let w = convertToInches(styles.width, 'width');
  let h = convertToInches(styles.height, 'height');
  
  // Use elementProps for width/height if available (these can be updated via properties panel)
  if (elementProps.width) {
    w = convertToInches(elementProps.width, 'width');
  }
  if (elementProps.height) {
    h = convertToInches(elementProps.height, 'height');
  }
  
  // Ensure valid positioning
  x = Math.max(0, x);
  y = Math.max(0, y);
  
  // Ensure minimum dimensions
  w = Math.max(w, 0.25);
  h = Math.max(h, 0.25);
  
  // Determine shape type from the shape ID
  const shapeType = getShapeType(element.id, pptx);
  
  // Build base options for shape
  const shapeOptions = {
    x: x,
    y: y,
    w: w,
    h: h,
    // Tags the shape so the animation pass can find the id PptxGenJS assigns it.
    objectName: animObjectName(element.id)
  };
  
  // Get fill color from elementProps first (from properties panel), then fallback to styles
  // Shape.js stores default: isTransparent: false, backgroundColor: "#fff"
  const isTransparent = elementProps.isTransparent === true;
  
  // Priority: elementProps.backgroundColor > styles.backgroundColor > styles.background > default white
  let fillColor = null;
  if (elementProps.backgroundColor !== undefined) {
    fillColor = elementProps.backgroundColor;
  } else if (styles.backgroundColor) {
    fillColor = styles.backgroundColor;
  } else if (styles.background) {
    fillColor = styles.background;
  }
  
  if (isTransparent || !fillColor || fillColor === 'transparent' || fillColor === 'none') {
    shapeOptions.fill = { type: 'none' };
  } else {
    const color = convertColor(fillColor);
    if (color) {
      shapeOptions.fill = { color: color };
      
      // Handle opacity from elementProps (default is 100)
      const opacity = elementProps.opacity !== undefined ? elementProps.opacity : 100;
      if (opacity < 100) {
        shapeOptions.fill.transparency = 100 - opacity;
      }
    } else {
      // Default to white fill if color conversion fails
      shapeOptions.fill = { color: 'FFFFFF' };
    }
  }
  
  // Set border/line from elementProps (from properties panel)
  // Shape.js stores default: strokeWidth: 0.5, borderColor: "#000", strokeUnit: "px"
  const strokeWidth = elementProps.strokeWidth !== undefined ? parseFloat(elementProps.strokeWidth) : 0.5;
  const borderColor = elementProps.borderColor || styles.borderColor || '#000000';
  
  if (strokeWidth > 0) {
    const convertedBorderColor = convertColor(borderColor);
    shapeOptions.line = {
      color: convertedBorderColor || '000000',
      pt: strokeWidth  // strokeWidth is already in px which maps roughly to pt
    };
  } else {
    // No border if strokeWidth is 0 or negative
    shapeOptions.line = { type: 'none' };
  }
  
  // Handle rotation from elementProps (default is 0) or CSS transform
  const rotation = elementProps.rotation !== undefined ? elementProps.rotation : parseRotation(styles.transform);
  if (rotation && rotation !== 0) {
    shapeOptions.rotate = rotation;
  }
  
  // Handle shadow
  if (styles.boxShadow && styles.boxShadow !== 'none') {
    shapeOptions.shadow = {
      type: 'outer',
      blur: 3,
      offset: 2,
      angle: 45,
      color: '000000',
      opacity: 0.3
    };
  }
  
  // Check if shape has text content
  let hasTextContent = false;
  let textRuns = [];
  
  if (shape.delta && shape.delta.ops && shape.delta.ops.length > 0) {
    hasTextContent = shape.delta.ops.some(op => 
      op.insert && typeof op.insert === 'string' && op.insert.trim() !== '' && op.insert !== '\n'
    );
    
    if (hasTextContent) {
      textRuns = parseDeltaToText(shape.delta, styles);
    }
  }
  
  // If shape has text, use addText with shape option to create grouped element
  // This makes text and shape behave as a single unit (like Google Slides)
  if (hasTextContent && textRuns.length > 0) {
    try {
      // Combine shape options with text options
      const textWithShapeOptions = {
        ...shapeOptions,
        shape: shapeType,  // This groups the text with the shape!
        valign: 'middle',
        align: 'center',
        wrap: true
      };
      
      slide.addText(textRuns, textWithShapeOptions);
    } catch (err) {
      console.error(`Error adding shape with text ${element.id}:`, err);
      // Fallback: try adding shape without text
      try {
        slide.addShape(shapeType, shapeOptions);
      } catch (fallbackErr) {
        console.error(`Fallback also failed for ${element.id}:`, fallbackErr);
      }
    }
  } else {
    // No text content - just add the shape
    try {
      slide.addShape(shapeType, shapeOptions);
    } catch (shapeErr) {
      console.error(`Error adding shape ${element.id}:`, shapeErr);
      // Try with a fallback rectangle shape
      try {
        slide.addShape(pptx.ShapeType.rect, shapeOptions);
      } catch (fallbackErr) {
        console.error(`Fallback also failed for ${element.id}:`, fallbackErr);
      }
    }
  }
};

/**
 * Export presentation with custom options
 * @param {Object} slides - Slides data
 * @param {string} fileName - File name
 * @param {Object} options - Custom options
 * @returns {Promise<Object>} Export result
 */
export const exportToPPTXWithOptions = async (slides, fileName, options = {}) => {
  const defaultOptions = {
    author: "MileSlide",
    company: "MileSlide",
    subject: "Presentation",
    includeMasterSlides: false,
    quality: 'high'
  };
  
  return exportToPPTX(slides, fileName, { ...defaultOptions, ...options });
};

/**
 * Get export preview information
 * @param {Object} slides - Slides data
 * @returns {Object} Preview information
 */
export const getExportPreview = (slides) => {
  if (!slides) return { slideCount: 0, elements: 0 };
  
  const exportSlides = Object.fromEntries(
    Object.entries(slides).filter(([key]) => !key.toLowerCase().includes('master'))
  );
  
  let totalElements = 0;
  Object.values(exportSlides).forEach(slide => {
    totalElements += (slide.textBoxIds?.length || 0);
    totalElements += (slide.imageIds?.length || 0);
    totalElements += Object.keys(slide.shapes || {}).length;
  });
  
  return {
    slideCount: Object.keys(exportSlides).length,
    elements: totalElements
  };
};

/**
 * Export slides to PDF format using existing mirror slide images
 * @param {Object} slides - Slides data from Redux store
 * @param {Object} mirrorSlides - Mirror slides data URLs from Redux store
 * @param {string} fileName - Name for the exported file
 * @param {Object} options - Export options
 * @returns {Promise<Object>} Result object with success status and message
 */
export const exportToPDF = async (slides, mirrorSlides, fileName = "presentation", options = {}) => {
  try {
    if (!slides || Object.keys(slides).length === 0) {
      return { success: false, message: "No slides to export." };
    }
    
    if (!mirrorSlides || Object.keys(mirrorSlides).length === 0) {
      return { success: false, message: "No slide previews available. Please refresh and try again." };
    }
    
    // Filter out master slides from export
    const allSlideKeys = Object.keys(slides);
    const slideKeys = options.includeMasterSlides 
      ? allSlideKeys 
      : allSlideKeys.filter(key => !key.toLowerCase().includes('master'));
    
    if (slideKeys.length === 0) {
      return { success: false, message: "No valid slides to export (master slides excluded)." };
    }
    
    // Create PDF with A4 dimensions (210 x 297 mm)
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // A4 landscape dimensions in mm
    const pageWidth = 297;
    const pageHeight = 210;
    
    // Process each slide
    for (let slideIndex = 0; slideIndex < slideKeys.length; slideIndex++) {
      const slideId = slideKeys[slideIndex];
      const slideData = slides[slideId];
      const mirrorDataUrl = mirrorSlides[slideId];
      
      if (!slideData) {
        console.warn(`Slide ${slideId} has no data, skipping...`);
        continue;
      }
      
      if (!mirrorDataUrl) {
        console.warn(`Slide ${slideId} has no preview image, skipping...`);
        continue;
      }
      
      try {
        // Add new page for each slide (except first)
        if (slideIndex > 0) {
          pdf.addPage();
        }
        
        // Calculate dimensions to fit the slide in the PDF page while maintaining aspect ratio
        const slideAspectRatio = 16 / 9; // Standard slide aspect ratio
        const pdfAspectRatio = pageWidth / pageHeight;
        
        let imgWidth, imgHeight, xOffset, yOffset;
        
        if (slideAspectRatio > pdfAspectRatio) {
          // Slide is wider than PDF page
          imgWidth = pageWidth;
          imgHeight = pageWidth / slideAspectRatio;
          xOffset = 0;
          yOffset = (pageHeight - imgHeight) / 2;
        } else {
          // Slide is taller than PDF page
          imgHeight = pageHeight;
          imgWidth = pageHeight * slideAspectRatio;
          yOffset = 0;
          xOffset = (pageWidth - imgWidth) / 2;
        }
        
        // Convert SVG data URL to PNG for PDF compatibility
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        // Set canvas size to match the desired dimensions
        canvas.width = 960; // Standard slide width
        canvas.height = 540; // Standard slide height
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            // Draw SVG to canvas
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve();
          };
          img.onerror = reject;
          img.src = mirrorDataUrl;
        });
        
        // Get PNG data URL from canvas
        const pngDataUrl = canvas.toDataURL('image/png', 0.95);
        
        // Add image to PDF
        pdf.addImage(pngDataUrl, 'PNG', xOffset, yOffset, imgWidth, imgHeight);
        
        console.warn(`Processed slide ${slideIndex + 1}/${slideKeys.length}: ${slideId}`);
        
      } catch (slideError) {
        console.error(`Error processing slide ${slideId}:`, slideError);
        // Continue with other slides
      }
    }
    
    // Generate and download the file
    const outputFileName = fileName.endsWith('.pdf') ? fileName.slice(0, -4) : fileName;
    pdf.save(`${outputFileName}.pdf`);
    
    return { 
      success: true, 
      message: `PDF file exported successfully! (${slideKeys.length} slides)` 
    };
    
  } catch (error) {
    console.error("Error exporting to PDF:", error);
    return { 
      success: false, 
      message: `Failed to export PDF file: ${error.message}`, 
      error 
    };
  }
};
